const NON_ALPHANUMERIC_RUN_PATTERN = /[^a-z0-9]+/g

/**
 * Preview fixtures for the Configuration workspace canvas.
 *
 * Each fixture is shaped like the widget's feed item (what FeedCard /
 * CompactCard consume). The pool is curated so no single item combines
 * flags that visually collide in the widget — e.g. `post_type: 'promotion'`
 * (absolute top-right Promoted badge) is never set on the same item that
 * also wants `canFollow` (Follow button in the same header corner).
 *
 * Media examples are sampled from the Bally's production feed API so the
 * Design preview exercises real creator video, still art, avatars, and bet
 * leg density instead of synthetic gradient placeholders.
 */

const MEDIA = {
  avatars: {
    propPick: 'https://storage.googleapis.com/wager-static-assets/streakers/profile_picture/desertdiamond/proppick.png',
    andrew: 'https://storage.googleapis.com/wager-static-assets/streakers/profile_picture/desertdiamond/AndrewMcInnis.jpg',
    terry: 'https://storage.googleapis.com/wager-static-assets/streakers/profile_picture/desertdiamond/TerryE.jpg',
    driving: 'https://storage.googleapis.com/wager-static-assets/streakers/profile_picture/desertdiamond/callingtheline%20(1).jpg',
    millz: 'https://storage.googleapis.com/wager-static-assets/profile-pictures/MillzPropbets.png',
    bill: 'https://storage.googleapis.com/wager-static-assets/streakers/profile_picture/desertdiamond/billha.jpeg',
    trends: 'https://storage.googleapis.com/wager-static-assets/streakers/profile_picture/desertdiamond/trendscenterappprofpic.jpeg',
  },
  images: {
    hockeyParlay: 'https://cdn.wager.games/streakers/feed_image/5227dce4-a409-4519-92ff-fdcfdd828191.png',
    homeRunParlay: 'https://cdn.wager.games/streakers/feed_image/15f340bb-6b77-464a-99aa-8d25d694812e%20%281%29.png',
    nbaProps: 'https://cdn.wager.games/streakers/feed_image/4f807a35-33f4-4ec6-8615-6e4e37febd0a.png',
    guardians: 'https://cdn.wager.games/streakers/feed_image/Screenshot%202026-04-29%20014025.png',
    totals: 'https://cdn.wager.games/streakers/feed_image/Screenshot%202026-04-29%20011127.png',
  },
  videos: {
    wild: 'https://cdn.wager.games/streakers/feed_video/andrew%20wild%20428.mp4',
    robertson: 'https://cdn.wager.games/streakers/feed_video/teppicks%20robertson%20428.mp4',
    alvarez: 'https://cdn.wager.games/streakers/feed_video/driving%20428%20alvarez%20hr.mp4',
    strikeoutParlay: 'https://cdn.wager.games/streakers/feed_video/millz0429.mp4',
    knicksParlay: 'https://cdn.wager.games/streakers/feed_video/0421%2819%29.mp4',
  },
}

function american(value) {
  if (value === undefined || value === null || value === '') return ''
  const text = String(value)
  return text.startsWith('+') || text.startsWith('-') ? text : `+${text}`
}

function leg({ outcomeId, label, event, market, odds, result }) {
  return {
    event_id: String(outcomeId),
    event: { id: String(outcomeId), name: event },
    market: { name: market },
    selection: {
      name: label,
      odds: { american: american(odds) },
      outcome_id: outcomeId,
    },
    result,
  }
}

function ogLeg({
  contractId,
  eventId,
  event,
  market,
  selection,
  chance,
  yesPrice,
  symbol,
  detailUrl,
  marketType = 'prediction',
}) {
  const contractID = String(contractId)
  const eventID = String(eventId)
  return {
    id: contractID,
    event_id: eventID,
    label: selection,
    odds: {},
    event: {
      id: eventID,
      name: event,
      status: 'active',
    },
    market: {
      id: `${eventID}-${market.toLowerCase().replace(NON_ALPHANUMERIC_RUN_PATTERN, '-')}`,
      name: market,
      type: marketType,
    },
    selection: {
      id: contractID,
      name: selection,
      chance,
    },
    provider: {
      key: 'og',
      selection_id: contractID,
      event_id: eventID,
      payload: {
        provider: 'og',
        contract_id: contractID,
        symbol,
        side: 'yes',
        chance,
        yes_price: yesPrice,
        detail_url: detailUrl,
        event_title: event,
        market_title: market,
        market_type: marketType,
      },
    },
  }
}

/* -------------------- Feed card fixtures -------------------- */

const timestampHoursAgo = (hours) => new Date(Date.now() - hours * 60 * 60 * 1000).toISOString()

function feedVideoParlay(ctaLabel) {
  return {
    id: 'fix-video-parlay',
    created_at: timestampHoursAgo(3),
    payload: {
      post_id: 'fix-video-parlay',
      preview_wager_kind: 'same_game_parlay',
      text: 'Strikeout fest incoming. Both starting pitchers should deal today.',
      user: { id: 'creator-millz', display_name: 'MillzPropbets', avatar_url: MEDIA.avatars.millz },
      cta: { kind: 'url', label: ctaLabel, target: 'https://example.com/tail' },
      media_objects: [
        { type: 'video', url: MEDIA.videos.strikeoutParlay, metadata: { poster_url: MEDIA.images.homeRunParlay } },
      ],
      tags: [
        { id: 'tg-expert', position: 'avatar-header', label: 'Creator', bg_color: '#0f172a', text_color: '#f8fafc' },
        { id: 'tg-mlb', position: 'below-header', label: 'MLB', bg_color: '#dbeafe', text_color: '#1d4ed8' },
      ],
      legs: [
        leg({
          outcomeId: 4164021001,
          label: 'Jacob deGrom Over 5.5 Strikeouts thrown by the Player - Including Extra Innings',
          event: 'Texas Rangers - New York Yankees',
          market: 'Pitcher Strikeouts',
          odds: '-345',
        }),
        leg({
          outcomeId: 4164021002,
          label: 'Cam Schlittler Over 4.5 Strikeouts thrown by the Player - Including Extra Innings',
          event: 'Texas Rangers - New York Yankees',
          market: 'Pitcher Strikeouts',
          odds: '-360',
        }),
      ],
    },
  }
}

function feedImageBet(ctaLabel) {
  return {
    id: 'fix-image-bet',
    created_at: timestampHoursAgo(7),
    payload: {
      post_id: 'fix-image-bet',
      preview_wager_kind: 'multiple_singles',
      text: 'Both lean on hot form, major role, and special-teams involvement.',
      user: { id: 'creator-prop-pick', display_name: 'Prop.Pick', avatar_url: MEDIA.avatars.propPick },
      cta: { kind: 'url', label: ctaLabel, target: 'https://example.com/watch' },
      media_objects: [{ type: 'image', url: MEDIA.images.hockeyParlay }],
      tags: [
        { id: 'tg-nhl', position: 'above-content', label: 'NHL', bg_color: '#dbeafe', text_color: '#1d4ed8' },
      ],
      legs: [
        leg({
          outcomeId: 4160279552,
          label: 'Alex Tuch - Yes To score - Including Overtime',
          event: 'Buffalo Sabres - Boston Bruins',
          market: 'Anytime Goal',
          odds: '175',
        }),
        leg({
          outcomeId: 4161226308,
          label: 'Jackson LaCombe - Yes Player to record at least 1 assists - Including Overtime',
          event: 'Edmonton Oilers - Anaheim Ducks',
          market: 'Player Assists',
          odds: '-130',
        }),
      ],
    },
  }
}

function feedPromoCard(ctaLabel) {
  return {
    id: 'fix-promo',
    created_at: timestampHoursAgo(1),
    payload: {
      post_id: 'fix-promo',
      post_type: 'promotion',
      text: 'Power bats ready to go yard in tonight\'s featured home run parlay.',
      user: { id: 'creator-prop-pick', display_name: 'Prop.Pick', avatar_url: MEDIA.avatars.propPick },
      cta: { kind: 'url', label: ctaLabel, target: 'https://example.com/promo' },
      media_objects: [{ type: 'image', url: MEDIA.images.homeRunParlay }],
      tags: [],
      legs: [
        leg({
          outcomeId: 4161226309,
          label: 'Junior Caminero - Yes Player to Hit a Home Run - Including Extra Innings',
          event: 'Cleveland Guardians - Tampa Bay Rays',
          market: 'Player Home Runs',
          odds: '350',
        }),
        leg({
          outcomeId: 4161226310,
          label: 'Brandon Lowe - Yes Player to Hit a Home Run - Including Extra Innings',
          event: 'Pittsburgh Pirates - St. Louis Cardinals',
          market: 'Player Home Runs',
          odds: '800',
        }),
      ],
    },
  }
}

function feedTextPick() {
  return {
    id: 'fix-text',
    created_at: timestampHoursAgo(12),
    payload: {
      post_id: 'fix-text',
      text: "Total has dropped to the lowest number of the series, creating value if the pace holds.",
      user: { id: 'creator-trends', display_name: 'TrendsCenter', avatar_url: MEDIA.avatars.trends },
      cta: null,
      media_objects: [],
      tags: [
        { id: 'tg-take', position: 'avatar-header', label: 'Take', bg_color: '#fef3c7', text_color: '#78350f' },
      ],
      legs: [],
    },
  }
}

function feedLiveClip(ctaLabel) {
  return {
    id: 'fix-live-clip',
    created_at: timestampHoursAgo(2),
    payload: {
      post_id: 'fix-live-clip',
      text: 'Wild showing a deeper, more balanced offensive attack.',
      user: { id: 'creator-andrew', display_name: 'Andrew.McInnis', avatar_url: MEDIA.avatars.andrew },
      cta: { kind: 'url', label: ctaLabel, target: 'https://example.com/clip' },
      media_objects: [
        { type: 'video', url: MEDIA.videos.wild, metadata: { poster_url: MEDIA.images.totals } },
      ],
      tags: [
        { id: 'tg-nhl-live', position: 'below-header', label: 'NHL', bg_color: '#dbeafe', text_color: '#1d4ed8' },
        { id: 'tg-live', position: 'bet-topline', label: 'LIVE', bg_color: '#ef4444', text_color: '#ffffff' },
      ],
      legs: [
        leg({
          outcomeId: 4160279553,
          label: 'MIN Wild Moneyline - Including Overtime',
          event: 'Dallas Stars - Minnesota Wild',
          market: 'Moneyline',
          odds: '+105',
        }),
      ],
    },
  }
}

/* -------------------- OG prediction-market fixtures -------------------- */

function ogVideoChampion() {
  const detailUrl = 'https://og.com/markets/pro-baseball-champion-2026-qqyKsX'
  return {
    id: 'fix-og-video-champion',
    created_at: timestampHoursAgo(2),
    payload: {
      post_id: 'fix-og-video-champion',
      text: 'Arizona has the price profile we want in a wide-open title market.',
      user: { id: 'creator-millz', display_name: 'MillzPropbets', avatar_url: MEDIA.avatars.millz },
      cta: { kind: 'provider_cart', label: 'Trade Arizona', target: detailUrl },
      media_objects: [
        { type: 'video', url: MEDIA.videos.strikeoutParlay, metadata: { poster_url: MEDIA.images.homeRunParlay } },
      ],
      tags: [
        { id: 'tg-og', position: 'below-header', label: 'OG', bg_color: 'rgba(249, 98, 0, 0.12)', text_color: '#F96200' },
      ],
      legs: [
        ogLeg({
          contractId: 'og-preview-arizona-champion',
          eventId: 'og-preview-pro-baseball-champion-2026',
          event: 'Pro Baseball Champion 2026',
          market: 'Pro Baseball Champion 2026',
          selection: 'Arizona',
          chance: '2',
          yesPrice: '0.02',
          symbol: 'OG-MLB-CHAMPION-2026-ARI',
          detailUrl,
        }),
      ],
    },
  }
}

function ogImageSportsWinner() {
  const detailUrl = 'https://og.com/markets/colorado-at-pittsburgh-2026-05-12-tNC1_d'
  return {
    id: 'fix-og-image-sports',
    created_at: timestampHoursAgo(4),
    payload: {
      post_id: 'fix-og-image-sports',
      text: 'Market is leaning Pittsburgh before first pitch.',
      user: { id: 'creator-prop-pick', display_name: 'Prop.Pick', avatar_url: MEDIA.avatars.propPick },
      cta: { kind: 'provider_cart', label: 'Trade Pittsburgh', target: detailUrl },
      media_objects: [{ type: 'image', url: MEDIA.images.guardians }],
      tags: [],
      legs: [
        ogLeg({
          contractId: 'og-preview-pittsburgh-winner',
          eventId: 'og-preview-colorado-pittsburgh',
          event: 'Colorado @ Pittsburgh',
          market: 'Winner',
          selection: 'Pittsburgh',
          chance: '71',
          yesPrice: '0.71',
          symbol: 'OG-MLB-COL-PIT-WINNER-PIT',
          detailUrl,
          marketType: 'moneyline',
        }),
      ],
    },
  }
}

function ogVideoCryptoPrediction() {
  const detailUrl = 'https://og.com/markets/when-will-bitcoin-cross-100k-again-57scX3'
  return {
    id: 'fix-og-video-crypto',
    created_at: timestampHoursAgo(6),
    payload: {
      post_id: 'fix-og-video-crypto',
      text: 'A macro catalyst can move this contract quickly.',
      user: { id: 'creator-driving', display_name: 'Driving.the.Line', avatar_url: MEDIA.avatars.driving },
      cta: { kind: 'provider_cart', label: 'Trade Before January 2027', target: detailUrl },
      media_objects: [{ type: 'video', url: MEDIA.videos.alvarez, metadata: { poster_url: MEDIA.images.totals } }],
      tags: [],
      legs: [
        ogLeg({
          contractId: 'og-preview-btc-before-jan-2027',
          eventId: 'og-preview-bitcoin-100k',
          event: 'When will bitcoin cross 100k again?',
          market: 'Bitcoin 100k timing',
          selection: 'Before January 2027',
          chance: '63',
          yesPrice: '0.63',
          symbol: 'OG-BTC-100K-BEFORE-2027',
          detailUrl,
        }),
      ],
    },
  }
}

function ogImageCompanyPrediction() {
  const detailUrl = 'https://og.com/markets/largest-company-end-of-june-2026-JnZ1mf'
  return {
    id: 'fix-og-image-company',
    created_at: timestampHoursAgo(8),
    payload: {
      post_id: 'fix-og-image-company',
      text: 'AI demand keeps the top-company race live through June.',
      user: { id: 'creator-trends', display_name: 'TrendsCenter', avatar_url: MEDIA.avatars.trends },
      cta: { kind: 'provider_cart', label: 'Trade NVIDIA', target: detailUrl },
      media_objects: [{ type: 'image', url: MEDIA.images.nbaProps }],
      tags: [],
      legs: [
        ogLeg({
          contractId: 'og-preview-largest-company-nvidia',
          eventId: 'og-preview-largest-company-june-2026',
          event: 'Largest company end of June 2026',
          market: 'Largest company',
          selection: 'NVIDIA',
          chance: '39',
          yesPrice: '0.39',
          symbol: 'OG-LARGEST-COMPANY-JUN-2026-NVDA',
          detailUrl,
        }),
      ],
    },
  }
}

/* -------------------- Compact card fixtures -------------------- */

function compactMlb(ctaLabel) {
  return {
    id: 'fix-compact-mlb',
    created_at: timestampHoursAgo(1),
    payload: {
      post_id: 'fix-compact-mlb',
      preview_wager_kind: 'same_game_parlay',
      text: 'Knicks vs Hawks. Game Five!',
      user: { id: 'creator-bill', display_name: 'BillHarrelson', avatar_url: MEDIA.avatars.bill },
      cta: { kind: 'url', label: ctaLabel, target: 'https://example.com/watch' },
      media_objects: [{ type: 'video', url: MEDIA.videos.knicksParlay, metadata: { poster_url: MEDIA.images.nbaProps } }],
      tags: [],
      legs: [
        leg({
          outcomeId: 4162220101,
          label: 'Nickeil Alexander-Walker - Yes 3+ Three-Point Field Goals Made By The Player - Including Overtime',
          event: 'New York Knicks - Atlanta Hawks',
          market: 'Made Threes',
          odds: '110',
        }),
        leg({
          outcomeId: 4162220102,
          label: 'Karl-Anthony Towns - Yes 4+ Assists By The Player - Including Overtime',
          event: 'New York Knicks - Atlanta Hawks',
          market: 'Player Assists',
          odds: '116',
        }),
        leg({
          outcomeId: 4162220103,
          label: 'Jalen Brunson - Yes 25+ Points Scored By The Player - Including Overtime',
          event: 'New York Knicks - Atlanta Hawks',
          market: 'Player Points',
          odds: '-180',
        }),
      ],
    },
  }
}

function compactNba(ctaLabel) {
  return {
    id: 'fix-compact-nba',
    created_at: timestampHoursAgo(4),
    payload: {
      post_id: 'fix-compact-nba',
      text: 'Prime matchup spot to take advantage of pitching mistakes.',
      user: { id: 'creator-driving', display_name: 'Driving.the.Line', avatar_url: MEDIA.avatars.driving },
      cta: { kind: 'url', label: ctaLabel, target: 'https://example.com/tail' },
      media_objects: [{ type: 'video', url: MEDIA.videos.alvarez, metadata: { poster_url: MEDIA.images.guardians } }],
      tags: [],
      legs: [
        leg({
          outcomeId: 4162220201,
          label: 'Yordan Alvarez - Yes Player to Hit a Home Run - Including Extra Innings',
          event: 'Baltimore Orioles - Houston Astros',
          market: 'Player Home Runs',
          odds: '460',
        }),
      ],
    },
  }
}

function compactPromo(ctaLabel) {
  return {
    id: 'fix-compact-promo',
    created_at: timestampHoursAgo(2),
    payload: {
      post_id: 'fix-compact-promo',
      post_type: 'promotion',
      text: 'Consistent minutes and shot volume keep his scoring floor intact.',
      user: { id: 'creator-prop-pick', display_name: 'Prop.Pick', avatar_url: MEDIA.avatars.propPick },
      cta: { kind: 'url', label: ctaLabel, target: 'https://example.com/promo' },
      media_objects: [{ type: 'image', url: MEDIA.images.nbaProps }],
      tags: [],
      legs: [
        leg({
          outcomeId: 4162220301,
          label: "De'Aaron Fox Over 17.5 Points scored by the player",
          event: 'San Antonio Spurs - Portland Trail Blazers',
          market: 'Player Points',
          odds: '-120',
        }),
        leg({
          outcomeId: 4162220302,
          label: 'Kelly Oubre Jr. - Yes 10+ Points Scored By The Player - Including Overtime',
          event: 'Boston Celtics - Philadelphia 76ers',
          market: 'Player Points',
          odds: '120',
        }),
      ],
    },
  }
}

function compactBelmont(ctaLabel) {
  return {
    id: 'fix-compact-belmont',
    created_at: timestampHoursAgo(6),
    payload: {
      post_id: 'fix-compact-belmont',
      text: 'Consistent volume and top-line role drive chances.',
      user: { id: 'creator-terry', display_name: 'Terry.Edelmann', avatar_url: MEDIA.avatars.terry },
      cta: { kind: 'url', label: ctaLabel, target: 'https://example.com/race' },
      media_objects: [{ type: 'video', url: MEDIA.videos.robertson, metadata: { poster_url: MEDIA.images.hockeyParlay } }],
      tags: [],
      legs: [
        leg({
          outcomeId: 4162220401,
          label: 'Jason Robertson - Yes To score - Including Overtime',
          event: 'Dallas Stars - Minnesota Wild',
          market: 'Anytime Goal',
          odds: '123',
        }),
      ],
    },
  }
}

function ogCompactChampion() {
  const base = ogVideoChampion()
  return {
    ...base,
    id: 'fix-og-compact-champion',
    payload: {
      ...base.payload,
      post_id: 'fix-og-compact-champion',
      text: 'Long-shot title price, clean single-contract CTA.',
    },
  }
}

function ogCompactSportsWinner() {
  const base = ogImageSportsWinner()
  return {
    ...base,
    id: 'fix-og-compact-sports',
    payload: {
      ...base.payload,
      post_id: 'fix-og-compact-sports',
      text: 'Pittsburgh remains the sharper side.',
    },
  }
}

/* -------------------- Avatars / stories -------------------- */

const STORIES = [
  { id: 'story-prop-pick', label: 'Prop.Pick', avatar: MEDIA.avatars.propPick, ringStart: '#2563eb', ringEnd: '#22d3ee' },
  { id: 'story-andrew', label: 'Andrew', avatar: MEDIA.avatars.andrew, ringStart: '#16a34a', ringEnd: '#facc15' },
  { id: 'story-terry', label: 'Terry', avatar: MEDIA.avatars.terry, ringStart: '#ec4899', ringEnd: '#f97316' },
  { id: 'story-driving', label: 'Driving', avatar: MEDIA.avatars.driving, ringStart: '#f59e0b', ringEnd: '#ef4444' },
  { id: 'story-millz', label: 'Millz', avatar: MEDIA.avatars.millz, ringStart: '#6366f1', ringEnd: '#14b8a6' },
  { id: 'story-trends', label: 'Trends', avatar: MEDIA.avatars.trends, ringStart: '#0ea5e9', ringEnd: '#2563eb' },
]

/* -------------------- Public builder -------------------- */

function normalizeCtaKind(kind) {
  if (kind === 'provider-cart') return 'provider_cart'
  if (kind === 'deep-link') return 'deep_link'
  return kind
}


function buildPreviewSelectionSet(legs, wagerKind) {
  if (!legs.length || !['same_game_parlay', 'multiple_singles'].includes(wagerKind)) return undefined

  const selections = legs.map((sourceLeg) => ({
    id: sourceLeg.selection.id,
    sort_order: sourceLeg.sort_order,
    label: sourceLeg.label,
    subtitle: sourceLeg.subtitle,
    status: sourceLeg.status || 'active',
    odds: sourceLeg.odds,
    market: sourceLeg.market,
    event: sourceLeg.event,
    provider_ref: sourceLeg.provider,
  }))
  const entries = []
  if (wagerKind === 'multiple_singles') {
    for (const selection of selections) {
      entries.push({
        id: `entry:single:${selection.provider_ref.selection_id}`,
        kind: 'single',
        label: 'single',
        selection_ids: [selection.id],
        provider_key: 'kambi',
        coupon_type: 'single',
        outcome_ids: [selection.provider_ref.selection_id],
        action: {
          type: 'betslip',
          provider_key: 'kambi',
          coupon_type: 'single',
          outcome_ids: [selection.provider_ref.selection_id],
        },
      })
    }
  } else {
    const selectionIds = selections.map((selection) => selection.id)
    const outcomeIds = selections.map((selection) => selection.provider_ref.selection_id)
    entries.push({
      id: 'entry:preview-sgp',
      kind: 'same_game_parlay',
      label: 'parlay',
      selection_ids: selectionIds,
      group_ids: ['group:preview-sgp'],
      provider_key: 'kambi',
      coupon_type: 'combination',
      outcome_ids: outcomeIds,
      action: {
        type: 'betslip',
        provider_key: 'kambi',
        coupon_type: 'combination',
        outcome_ids: outcomeIds,
      },
    })
  }

  return {
    schema_version: '2.0',
    source: { provider_key: 'kambi', provider_name: 'Kambi' },
    summary: {
      kind: wagerKind === 'same_game_parlay' ? 'same_game_parlay' : 'mixed',
      label: wagerKind === 'same_game_parlay' ? 'parlay' : `${entries.length} wagers`,
      selection_count: selections.length,
      status: 'active',
    },
    selections,
    groups: wagerKind === 'same_game_parlay'
      ? [{
          id: 'group:preview-sgp',
          kind: 'same_game',
          label: selections[0]?.event?.name || 'Same game',
          selection_ids: selections.map((selection) => selection.id),
          relationship: 'same_event',
        }]
      : [],
    entries,
  }
}

function toCanonicalPreviewItem(item) {
  const payload = item.payload || item
  const id = item.id || payload.id
  const legs = (payload.legs || payload.bet_legs || []).map((sourceLeg, index) => {
    const selection = sourceLeg.selection || {}
    const market = sourceLeg.market || {}
    const event = sourceLeg.event || {}
    const providerPayload = sourceLeg.provider?.payload || sourceLeg.provider_payload || sourceLeg.cart_item || selection.provider_payload || {}
    const providerSelectionId = sourceLeg.provider?.selection_id || selection.provider_selection_id || selection.outcome_id || sourceLeg.outcome_id || ''
    return {
      id: sourceLeg.id || `${id}-leg-${index}`,
      sort_order: index,
      label: sourceLeg.label || selection.name || '',
      subtitle: sourceLeg.subtitle || market.name || market.type || '',
      status: sourceLeg.status || sourceLeg.result || '',
      odds: sourceLeg.odds || selection.odds || {},
      selection: {
        id: selection.id || `${id}-selection-${index}`,
        name: selection.name || sourceLeg.label || '',
        type: selection.type || '',
      },
      market: {
        id: market.id || `${id}-market-${index}`,
        name: market.name || '',
        type: market.type || '',
        points: market.points ?? null,
      },
      event: {
        id: event.id || sourceLeg.event_id || `${id}-event-${index}`,
        name: event.name || '',
        start_time: event.start_time || null,
        status: event.status || '',
        participants: {
          home: event.home || event.participants?.home || null,
          away: event.away || event.participants?.away || null,
        },
      },
      provider: {
        key: sourceLeg.provider?.key || providerPayload.provider || (providerSelectionId ? 'kambi' : ''),
        selection_id: providerSelectionId ? String(providerSelectionId) : '',
        event_id: String(sourceLeg.provider?.event_id || event.id || sourceLeg.event_id || ''),
        payload: providerPayload,
      },
    }
  })
  return {
    id,
    kind: payload.post_type === 'promotion' ? 'promotion' : (payload.kind || 'post'),
    text: payload.text || '',
    created_at: item.created_at || payload.created_at,
    expires_at: payload.expires_at || null,
    creator: payload.creator || payload.user || {},
    media: (payload.media || payload.media_objects || []).map((media, index) => {
      const metadata = media.metadata || {}
      const isVideo = media.type === 'video'
      return {
        id: media.id || `${id}-media-${index}`,
        type: media.type,
        url: metadata.hls_manifest_url || media.url,
        sort_order: index,
        playback: isVideo ? {
          hls_url: metadata.hls_manifest_url || null,
          fallback_url: metadata.fallback_url || media.url,
          poster_url: metadata.poster_url || null,
        } : undefined,
      }
    }),
    legs,
    selection_set: payload.selection_set || buildPreviewSelectionSet(legs, payload.preview_wager_kind),
    cta: payload.cta ? { ...payload.cta, kind: normalizeCtaKind(payload.cta.kind) } : null,
    tags: (payload.tags || []).map((tag) => ({
      label: tag.label || '',
      position: tag.position || null,
      style: tag.style || {
        background_color: tag.bg_color || null,
        text_color: tag.text_color || null,
        icon: tag.icon || null,
        font_style: tag.font_style || null,
      },
    })),
  }
}

/**
 * Builds the full showcase. Callers pick what they need from the returned
 * object:
 *   - feedItems[]:    distinct FeedCard-shaped posts.
 *   - compactItems[]: distinct CompactCard-shaped posts.
 *   - stories[]:      six creator avatars for circle_stories.
 *
 * Legacy single-item accessors (feedCard, compactCard) alias feedItems[0] /
 * compactItems[0] for callers that still want one primary.
 */
function buildOGPreviewShowcase() {
  const feedItems = [
    ogVideoChampion(),
    ogImageSportsWinner(),
    ogVideoCryptoPrediction(),
    ogImageCompanyPrediction(),
  ].map(toCanonicalPreviewItem)
  const compactItems = [
    ogCompactChampion(),
    ogCompactSportsWinner(),
  ].map(toCanonicalPreviewItem)
  return {
    feedItems,
    compactItems,
    stories: STORIES,
    feedCard: feedItems[0],
    compactCard: compactItems[0],
  }
}

export function buildPreviewShowcase(buttonText, options = {}) {
  const provider = typeof options === 'string' ? options : options?.provider
  if (provider === 'og') {
    return buildOGPreviewShowcase()
  }

  const cta = buttonText || 'Tail it!'
  const feedItems = [
    feedImageBet(cta),
    feedPromoCard(cta),
    feedVideoParlay(cta),
    feedTextPick(),
    feedLiveClip(cta),
  ].map(toCanonicalPreviewItem)
  const compactItems = [
    compactMlb(cta),
    compactNba(cta),
    compactPromo(cta),
    compactBelmont(cta),
  ].map(toCanonicalPreviewItem)
  return {
    feedItems,
    compactItems,
    stories: STORIES,
    feedCard: feedItems[0],
    compactCard: compactItems[0],
  }
}

export const PREVIEW_ASSETS = {
  avatar: MEDIA.avatars.propPick,
  poster: MEDIA.images.hockeyParlay,
  imageA: MEDIA.images.homeRunParlay,
  imageB: MEDIA.images.nbaProps,
  videoA: MEDIA.videos.strikeoutParlay,
  videoB: MEDIA.videos.wild,
}
